import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { likePlayableProperty, unlikePlayableProperty } from "@/services/LikeService";
import {
    getLikeRequestingUserKey,
    getPropertyLikeCountsQueryKey,
    PLAYABLE_PROPERTY_LIKE_TARGET_TYPE,
} from "@/constants/like.constants";
import { queuePendingLike } from "@/helpers/pending-like-storage";
import type {
    MixpanelLikeUnlikeContentType,
    MixpanelLikeUnlikeLocation,
} from "@/constants/mixpanel.constants";
import useMixpanelEvent from "@/hooks/useMixpanelEvent";
import { useUser } from "@/context/UserContext";

type LikeAction = "like" | "unlike";

interface UsePropertyLikeOptions {
    playablePropertyId: string;
    initialLikeCount?: number;
    initialLikedByCurrentUser?: boolean;
    userId: string | null;
    isAuthenticated: boolean;
    onRequireAuth: () => void;
    mixpanelLocation: MixpanelLikeUnlikeLocation;
    nicknameLiked?: string | null;
    contentType?: MixpanelLikeUnlikeContentType;
}

interface ToggleLikeOptions {
    onError?: (error?: unknown) => void;
}

function patchPuzzleFeedPages(
    prev: unknown,
    playablePropertyId: string,
    nextLiked: boolean,
    nextCount: number,
): unknown {
    const data = prev as { pages?: Array<{ data?: { data?: Array<Record<string, unknown>> } }> } | undefined;
    if (!data?.pages || !Array.isArray(data.pages)) {
        return prev;
    }
    const pages = data.pages.map((page) => {
        const items = page?.data?.data;
        if (!Array.isArray(items)) {
            return page;
        }
        const nextItems = items.map((item) => {
            if (String(item.id) !== playablePropertyId) {
                return item;
            }
            return {
                ...item,
                likeCount: nextCount,
                likedByCurrentUser: nextLiked,
            };
        });
        return {
            ...page,
            data: {
                ...page.data,
                data: nextItems,
            },
        };
    });
    return { ...data, pages };
}

/**
 * Optimistic property like toggle with puzzle feed and scoreboard cache sync.
 */
export default function usePropertyLike({
    playablePropertyId,
    initialLikeCount = 0,
    initialLikedByCurrentUser = false,
    userId,
    isAuthenticated,
    onRequireAuth,
    mixpanelLocation,
    nicknameLiked = null,
    contentType = "puzzle",
}: UsePropertyLikeOptions) {
    const queryClient = useQueryClient();
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const { userState } = useUser();
    const requestingUserKey = getLikeRequestingUserKey(userId);
    const [isLiked, setIsLiked] = React.useState(Boolean(initialLikedByCurrentUser));
    const [likeCount, setLikeCount] = React.useState(
        Number.isFinite(initialLikeCount) ? Math.max(0, Number(initialLikeCount)) : 0,
    );
    const lastHydratedSignatureRef = React.useRef<string | undefined>(undefined);

    const applyLikeStateToCaches = React.useCallback(
        (nextLiked: boolean, nextCount: number) => {
            queryClient.setQueriesData(
                {
                    queryKey: ["puzzle-feed"],
                    predicate: (query) =>
                        Array.isArray(query.queryKey) &&
                        query.queryKey[0] === "puzzle-feed" &&
                        String(query.queryKey[1] ?? "anonymous") === requestingUserKey,
                },
                (prev) => patchPuzzleFeedPages(prev, playablePropertyId, nextLiked, nextCount),
            );
            queryClient.setQueriesData(
                {
                    queryKey: ["puzzle-scoreboard-current-user"],
                    predicate: (query) =>
                        Array.isArray(query.queryKey) &&
                        query.queryKey[0] === "puzzle-scoreboard-current-user" &&
                        String(query.queryKey[1] ?? "") === playablePropertyId,
                },
                (prev: unknown) => {
                    if (!prev || typeof prev !== "object") {
                        return prev;
                    }
                    return {
                        ...(prev as Record<string, unknown>),
                        likeCount: nextCount,
                        likedByCurrentUser: nextLiked,
                    };
                },
            );
            queryClient.setQueryData(
                getPropertyLikeCountsQueryKey(playablePropertyId, requestingUserKey),
                (prev: unknown) => {
                    if (!prev || typeof prev !== "object") {
                        return {
                            counts: [
                                {
                                    targetType: PLAYABLE_PROPERTY_LIKE_TARGET_TYPE,
                                    targetId: playablePropertyId,
                                    count: nextCount,
                                    likedByCurrentUser: nextLiked,
                                },
                            ],
                        };
                    }
                    const counts = Array.isArray((prev as { counts?: unknown[] }).counts)
                        ? (prev as { counts: Array<Record<string, unknown>> }).counts
                        : [];
                    if (counts.length === 0) {
                        return {
                            counts: [
                                {
                                    targetType: PLAYABLE_PROPERTY_LIKE_TARGET_TYPE,
                                    targetId: playablePropertyId,
                                    count: nextCount,
                                    likedByCurrentUser: nextLiked,
                                },
                            ],
                        };
                    }
                    return {
                        counts: counts.map((item) =>
                            String(item.targetId) === playablePropertyId
                                ? { ...item, count: nextCount, likedByCurrentUser: nextLiked }
                                : item,
                        ),
                    };
                },
            );
        },
        [playablePropertyId, queryClient, requestingUserKey],
    );

    const mutation = useMutation({
        mutationFn: async (action: LikeAction) => {
            if (!userId) {
                throw new Error("Missing user id");
            }
            if (action === "like") {
                return likePlayableProperty(playablePropertyId, userId);
            }
            return unlikePlayableProperty(playablePropertyId, userId);
        },
        onSuccess: (_result, action) => {
            sendMixpanelEvent("Like-Unlike", {
                pageUrl: window.location.href,
                email_liker: userState?.emailAddress ?? null,
                nickname_liker: userState?.nickname ?? null,
                nickname_liked: nicknameLiked,
                action_type: action === "like" ? "Like" : "Unlike",
                content_type: contentType,
                location: mixpanelLocation,
            });
        },
    });

    // Keep local state in sync with the latest server/cache-derived props.
    // Skip syncing while the mutation is pending so optimistic UI isn't clobbered.
    React.useEffect(() => {
        if (mutation.isPending) {
            return;
        }
        const normalizedCount = Number.isFinite(initialLikeCount) ? Math.max(0, Number(initialLikeCount)) : 0;
        const normalizedLiked = Boolean(initialLikedByCurrentUser);
        const signature = `${playablePropertyId}:${requestingUserKey}:${normalizedCount}:${normalizedLiked}`;
        if (lastHydratedSignatureRef.current === signature) {
            return;
        }
        lastHydratedSignatureRef.current = signature;
        setIsLiked(normalizedLiked);
        setLikeCount(normalizedCount);
    }, [
        initialLikeCount,
        initialLikedByCurrentUser,
        mutation.isPending,
        playablePropertyId,
        requestingUserKey,
    ]);

    const toggleLike = ({ onError }: ToggleLikeOptions = {}) => {
        if (!isAuthenticated || !userId) {
            if (!isLiked) {
                queuePendingLike({
                    targetType: PLAYABLE_PROPERTY_LIKE_TARGET_TYPE,
                    targetId: playablePropertyId,
                });
            }
            onRequireAuth();
            return;
        }
        const nextLiked = !isLiked;
        const action: LikeAction = nextLiked ? "like" : "unlike";
        const previousLiked = isLiked;
        const previousCount = likeCount;
        const nextCount = Math.max(0, likeCount + (nextLiked ? 1 : -1));
        setIsLiked(nextLiked);
        setLikeCount(nextCount);
        applyLikeStateToCaches(nextLiked, nextCount);
        mutation.mutate(action, {
            onError: (error) => {
                setIsLiked(previousLiked);
                setLikeCount(previousCount);
                applyLikeStateToCaches(previousLiked, previousCount);
                if (onError) {
                    onError(error);
                    return;
                }
                toast.error("Could not update like right now. Please try again.", {
                    id: "PROPERTY_LIKE_UNAVAILABLE",
                });
            },
        });
    };

    return {
        isLiked,
        likeCount,
        isPending: mutation.isPending,
        toggleLike,
    };
}
